import React from "react"
import { LngLatBounds } from "mapbox-gl"
import { APIProvider } from "@vis.gl/react-google-maps"
import { Bbox } from "../../lib/helpers/map"
import { useFeatFlags } from "./FeatFlagProvider"
import { createGoogleMapBoundsFromBBox } from "../../lib/helpers/googleMap"
import { ProviderProps } from "./types"

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""

type MapboxBoundsType = LngLatBounds | undefined

type GMapBoundsType = google.maps.LatLngBounds | undefined

interface MapBBoxValueProps {
  currentBBox: Bbox | undefined
  setCurrentBBox: (bbox: Bbox | undefined) => void
  currentGMapBounds: GMapBoundsType
  setBBoxFromMapbox: (bounds: MapboxBoundsType) => void
  setGMapsApiLoaded: (loaded: boolean) => void
}

export const MapBBoxContext = React.createContext<MapBBoxValueProps | undefined>(undefined)

export const MapBBoxProvider: React.FC<ProviderProps> = ({ children }) => {
  const [currentBBox, setCurrentBBox] = React.useState<Bbox | undefined>(undefined)
  const [gMapsApiLoaded, setGMapsApiLoaded] = React.useState<boolean>(false)

  const setBBoxFromMapbox = (bounds: MapboxBoundsType) => {
    if (!bounds) {
      setCurrentBBox(undefined)
      return
    }
    const newBBox: Bbox = [
      [bounds.getSouthWest().lng, bounds.getSouthWest().lat],
      [bounds.getNorthEast().lng, bounds.getNorthEast().lat],
    ]
    setCurrentBBox(newBBox)
  }

  const currentGMapBounds = React.useMemo(() => {
    if (!currentBBox || !gMapsApiLoaded) return undefined
    return createGoogleMapBoundsFromBBox(currentBBox)
  }, [currentBBox, gMapsApiLoaded])

  const contextValue = React.useMemo(
    () => ({
      currentBBox,
      setCurrentBBox,
      currentGMapBounds,
      setBBoxFromMapbox,
      setGMapsApiLoaded,
    }),
    [currentBBox, currentGMapBounds],
  )

  const { google_maps } = useFeatFlags()

  if (!google_maps?.visible) {
    return <MapBBoxContext.Provider value={contextValue}>{children}</MapBBoxContext.Provider>
  }

  return (
    <APIProvider apiKey={API_KEY}>
      <MapBBoxContext.Provider value={contextValue}>{children}</MapBBoxContext.Provider>
    </APIProvider>
  )
}

export function useMapBBox() {
  const context = React.useContext(MapBBoxContext)
  if (!context) throw new Error("Must be called under MapBBoxContext")
  return context
}
